import { useEffect, useRef, type RefObject } from 'react';

import { getFocusable, handleTabTrap } from '@/components/layout/focus-trap';

/**
 * Comportamiento común de un diálogo modal (UX-A11Y-01): al abrir enfoca el primer
 * control, atrapa Tab, cierra con Escape (salvo `busy`) y bloquea el scroll del fondo;
 * al cerrar restaura el scroll y devuelve el foco al gatillo o, si ya no está usable,
 * al `<main>` (nunca al `<body>`).
 *
 * `onClose` y `busy` se leen por ref: el efecto corre SOLO al abrir/cerrar. Si
 * dependiera de ellos, cada re-render (p. ej. `busy` al enviar) robaría el foco.
 */
export function useModalDialog(
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  { onClose, busy = false }: { onClose: () => void; busy?: boolean },
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) {
      return;
    }
    const trigger = document.activeElement as HTMLElement | null;
    getFocusable(panelRef.current ?? document.body)[0]?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (!busyRef.current) {
          e.preventDefault();
          onCloseRef.current();
        }
        return;
      }
      handleTabTrap(e, panelRef.current);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      const t = trigger as HTMLButtonElement | null;
      if (t && t.isConnected && t !== document.body && !t.disabled) {
        t.focus();
      } else {
        document.querySelector<HTMLElement>('main')?.focus();
      }
    };
  }, [open, panelRef]);
}
